/**
 * Authenticated fetch wrapper that auto-injects the Authorization header
 * and forces sign-out on 401 responses. Clears the backend session cookie
 * via /api/auth/logout, then sends the browser to /auth?expired=1.
 */
export async function authFetch(
  url: string,
  accessToken: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  if (!headers.has("Content-Type") && init?.body && typeof init.body === "string") {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    let bodySnippet = "";
    try {
      const cloned = response.clone();
      const text = await cloned.text();
      bodySnippet = text.slice(0, 200);
    } catch {
      bodySnippet = "<unreadable>";
    }
    console.warn(
      `[authFetch] 401 → forcing sign-out: method=${init?.method ?? "GET"} url=${url} body=${bodySnippet}`,
    );
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    }).catch(() => undefined);
    if (typeof window !== "undefined") {
      // Bring the user back to the page they were on after sign-in.
      // The current path on /auth itself shouldn't loop back to itself,
      // and an unauth response from the auth pages means there's
      // nowhere meaningful to return to anyway.
      const { pathname, search } = window.location;
      const params = new URLSearchParams({ expired: "1" });
      if (pathname && !pathname.startsWith("/auth")) {
        params.set("callbackUrl", pathname + search);
      }
      window.location.assign(`/auth?${params.toString()}`);
    }
    throw new Error("Session expired");
  }

  return response;
}
